import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import {
  getListBlockedApiV1ConversationsBlockedGetQueryKey,
  getListConversationItemsApiV1ConversationItemsGetQueryKey,
  getListConversationsApiV1ConversationsGetQueryKey,
  getListFavoritesApiV1ConversationsFavoritesGetQueryKey,
  useConversationActionApiV1ConversationsActionPost,
} from '#/shared/api/generated/communication/theCargoCommunication'
import { ConversationActionType } from '#/shared/api/generated/communication/models'
import type { ConversationActionType as ActionType } from '#/shared/api/generated/communication/models'
import { getApiErrorDetail } from '#/shared/lib/api/error-handler'
import { toast } from '#/shared/ui/sonner'

export { ConversationActionType }

/** Where the action applies — a thread id and/or a counterparty number. */
export interface ActionTarget {
  conversationId?: string
  phone?: string
}

interface ConversationActions {
  apply: (action: ActionType, target: ActionTarget) => Promise<boolean>
  /** Apply an action to many threads at once (the select-mode bulk actions). */
  applyBulk: (action: ActionType, conversationIds: string[]) => Promise<boolean>
  isPending: boolean
}

/**
 * Context-menu actions (favorite, mute, block, mark read, delete) for a thread
 * or number. Each call invalidates the affected lists; success is silent (no
 * toast) and only failures surface, so callers just fire `apply(action, target)`.
 */
export function useConversationActions(): ConversationActions {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const mutation = useConversationActionApiV1ConversationsActionPost()

  const invalidate = () => {
    for (const queryKey of [
      getListConversationsApiV1ConversationsGetQueryKey(),
      getListConversationItemsApiV1ConversationItemsGetQueryKey(),
      getListFavoritesApiV1ConversationsFavoritesGetQueryKey(),
      getListBlockedApiV1ConversationsBlockedGetQueryKey(),
    ]) {
      void queryClient.invalidateQueries({ queryKey })
    }
  }

  const apply = async (
    action: ActionType,
    target: ActionTarget,
  ): Promise<boolean> => {
    try {
      await mutation.mutateAsync({
        data: {
          action,
          conversation_id: target.conversationId,
          phone_number: target.phone,
        },
      })
      invalidate()
      return true
    } catch (error) {
      toast.error(getApiErrorDetail(error) ?? t('phonePanel.actionToast.error'))
      return false
    }
  }

  const applyBulk = async (
    action: ActionType,
    conversationIds: string[],
  ): Promise<boolean> => {
    if (conversationIds.length === 0) return false
    try {
      await mutation.mutateAsync({
        data: { action, conversation_ids: conversationIds },
      })
      invalidate()
      return true
    } catch (error) {
      toast.error(getApiErrorDetail(error) ?? t('phonePanel.actionToast.error'))
      return false
    }
  }

  return { apply, applyBulk, isPending: mutation.isPending }
}
